import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../../services/api';
import InterventionCard from '../../components/admin/InterventionCard';
import './Analytics.css';

// "Who needs attention right now" — the college-wide answer, in one place
// (docs/UI_UX_ARCHITECTURE.md Admin Phase 5 §15). Reuses
// analyticsController.getCollegeDashboard's existing `intervention` block
// verbatim — the exact same flagged-student list already shown (buried) in
// the Dashboard/Analytics intervention tab, never a second computation.
const Interventions = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    API.get('/reports/dashboard')
      .then(res => setData(res.data))
      .catch(e => setErr(e.response?.data?.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="axh-page"><div className="axh-loading"><div className="axh-spinner" />Loading interventions…</div></div>;
  if (err || !data) return <div className="axh-page"><div className="axh-error">{err || 'No data'}</div></div>;
  if (data.scoped === false) return <div className="axh-page"><div className="axh-empty">{data.message}</div></div>;

  const { intervention: iv } = data;
  const students = iv.students || [];

  return (
    <div className="axh-page">
      <div className="axh-topbar">
        <div><h1>Interventions</h1><p>Students who need attention right now, across the whole college.</p></div>
      </div>

      <div className="axh-kpi-grid">
        <div className="axh-kpi"><div className="v" style={{ color: 'var(--z-danger)' }}>{iv.low_knowledge}</div><div className="l">Low Knowledge</div></div>
        <div className="axh-kpi"><div className="v" style={{ color: 'var(--z-danger)' }}>{iv.low_behaviour}</div><div className="l">Low Behaviour</div></div>
        <div className="axh-kpi"><div className="v" style={{ color: 'var(--z-danger)' }}>{iv.low_eri}</div><div className="l">Low Readiness</div></div>
        <div className="axh-kpi"><div className="v" style={{ color: 'var(--z-warning)' }}>{iv.declining_students}</div><div className="l">Declining</div></div>
      </div>

      <section className="axh-section">
        <div className="axh-section-head"><h2>Flagged Students</h2><p>Sorted by lowest placement readiness first</p></div>
        {students.length === 0 ? (
          <div className="axh-card"><div className="axh-empty">No students currently flagged for intervention.</div></div>
        ) : (
          <div className="axh-grid-2">
            {students.map(s => (
              <InterventionCard
                key={s.user_id}
                student={{
                  name: s.name || `${s.branch || 'Student'}`,
                  readiness_label: s.eri != null ? `${s.eri}% readiness` : null,
                  evidence: [
                    s.knowledge != null ? `Knowledge ${s.knowledge}%` : null,
                    s.behaviour != null ? `Behaviour ${s.behaviour}%` : null,
                  ].filter(Boolean).join(' · '),
                  primary_gap: (s.reasons || [])[0] || null,
                }}
                onView={() => navigate(`/admin/students/${s.user_id}/intelligence`)}
              />
            ))}
          </div>
        )}
      </section>

      <p className="axh-notes">
        <strong>Scope note:</strong> this view aggregates across your whole college. For a single
        batch's intervention list with topic-level detail, open that batch from the Dashboard's
        Batch Overview, then its Intervention tab.
      </p>
    </div>
  );
};

export default Interventions;
